import { useEffect, useState, useMemo } from "react";
import {
    Card,
    CardHeader,
    CardFooter,
    Pagination,
    Container,
    Row,
    Button,
    ButtonGroup,
    Col,
    Label,
    Input,
    Modal,
    ModalHeader,
    ModalBody,
    FormGroup,
    Spinner,
    Form,
} from "reactstrap";
import Header from "components/Headers/Header.js";
import Footer from "components/Footers/Footer.js";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import CustomTable from "components/CustomTable";
import { FaTimes, FaKey, FaFilePdf } from "react-icons/fa";
import copy from 'copy-to-clipboard';
import ReactPaginate from "react-paginate";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { apiURL1 } from '../../config';
import axios from "axios";

const SuperAdminManagement = () => {
    const token = localStorage.getItem("token");
    const navigate = useNavigate();
    const columns = ["ID", "Email", "Actions"];
    const [superAdminId, setSuperAdminId] = useState("");
    const [rows, setRows] = useState([]);
    const [addModal, setAddModal] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [changePasswordModal, setChangePasswordModal] = useState(false);
    const [showCredentialsModal, setShowCredentialsModal] = useState(false);
    const [newPassword, setNewPassword] = useState("");
    const [generatedCredentials, setGeneratedCredentials] = useState({ email: '', password: '' });
    const [addSuperAdminFormData, setAddSuperAdminFormData] = useState({
        email: "",
        password: "",
    });
    const [emailError, setEmailError] = useState("");
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [superAdmins, setSuperAdmins] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [filterEmail, setFilterEmail] = useState("");
    const [madrasaInfo, setMadrasaInfo] = useState({});

    useEffect(() => {
        fetchSuperAdmins();
        fetchMadrasaInfo();
    }, [currentPage]);

    const fetchMadrasaInfo = async () => {
        try {
            const response = await axios.get(`${apiURL1}/superadmin/school-settings`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = response.data;
            if (data && data.length > 0) {
                setMadrasaInfo(data[0]);
            }
        } catch (error) {
            console.error('Error fetching madrasa info:', error);
        }
    };

    const fetchSuperAdmins = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(
                `${apiURL1}/superadmin/getAllSuperAdmins?page=${currentPage + 1}&limit=10&search=${filterEmail}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const { superAdmins, pagination } = response.data;
            setSuperAdmins(superAdmins || []);
            setTotalCount(pagination?.total || 0);
            setIsLoading(false);
        } catch (error) {
            toast.error("Error fetching super admins");
            setSuperAdmins([]);
            setTotalCount(0);
            setIsLoading(false);
        }
    };

    const handlePageChange = (selectedPage) => {
        setCurrentPage(selectedPage.selected);
    };

    const addToggle = () => {
        setAddModal(!addModal);
        setAddSuperAdminFormData({ email: "", password: "" });
        setEmailError("");
    };

    const deleteToggle = () => setDeleteModal(!deleteModal);

    const changePasswordToggle = () => {
        setChangePasswordModal(!changePasswordModal);
        setNewPassword("");
    };

    const credentialsToggle = () => {
        setShowCredentialsModal(!showCredentialsModal);
    };

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleAddInputChange = (e) => {
        const { name, value } = e.target;
        setAddSuperAdminFormData({ ...addSuperAdminFormData, [name]: value });
        
        // Real-time email validation
        if (name === 'email') {
            if (value && !validateEmail(value)) {
                setEmailError("Please enter a valid email address");
            } else {
                setEmailError("");
            }
        }
    };

    const handleAddSubmit = async (e) => {
        e.preventDefault();
        
        // Validate email before submission
        if (!validateEmail(addSuperAdminFormData.email)) {
            setEmailError("Please enter a valid email address");
            return;
        }
        
        try {
            const response = await axios.post(
                `${apiURL1}/superadmin/register`,
                addSuperAdminFormData,
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
            );
            toast.success("Super admin created successfully");
            setGeneratedCredentials({ email: addSuperAdminFormData.email, password: addSuperAdminFormData.password });
            credentialsToggle();
            addToggle();
            setAddSuperAdminFormData({ email: "", password: "" });
            setEmailError("");
            fetchSuperAdmins();
        } catch (error) {
            toast.error(error.response?.data?.message || "Error creating super admin");
        }
    };

    const handleDelete = async () => {
        try {
            const response = await axios.delete(
                `${apiURL1}/superadmin/deleteSuperAdmin/${superAdminId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success("Super admin deleted successfully");
            setSuperAdmins(superAdmins.filter((admin) => admin._id !== superAdminId));
            deleteToggle();
        } catch (error) {
            toast.error(error.response?.data?.message || "Error deleting super admin");
        }
    };

    const handleChangePassword = async () => {
        try {
            const response = await axios.put(
                `${apiURL1}/superadmin/resetSuperAdminPassword`,
                { id: superAdminId, newPassword },
                { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
            );
            toast.success("Super admin password updated successfully");
            setGeneratedCredentials({
                email: superAdmins.find((admin) => admin._id === superAdminId)?.email,
                password: newPassword,
            });
            changePasswordToggle();
            credentialsToggle();
        } catch (error) {
            toast.error(error.response?.data?.message || "Error updating super admin password");
        }
    };

    const filteredSuperAdmins = useMemo(() => {
        return superAdmins.filter((admin) =>
            admin.email.toLowerCase().includes(filterEmail.toLowerCase())
        );
    }, [superAdmins, filterEmail]);

    const formatDate = (dateStr) => {
        const d = new Date(dateStr);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const reArrangeData = () => {
        const tempArr = filteredSuperAdmins.map((admin) => ({
            ID: admin._id,
            email: admin.email,
            actions: (
                <ButtonGroup size="sm">
                    <Button
                        size="sm"
                        color="danger"
                        onClick={() => {
                            deleteToggle();
                            setSuperAdminId(admin._id);
                        }}
                    >
                        <i className="fas fa-user-times"></i>
                    </Button>
                    <Button
                        size="sm"
                        color="info"
                        onClick={() => {
                            setSuperAdminId(admin._id);
                            changePasswordToggle();
                        }}
                    >
                        <FaKey />
                    </Button>
                </ButtonGroup>
            ),
        }));
        setRows(tempArr);
    };

    useEffect(() => {
        reArrangeData();
    }, [filteredSuperAdmins]);

    const handleCopyToClipboard = () => {
        copy(`${generatedCredentials.email}\n${generatedCredentials.password}`);
        toast.success("Credentials copied to clipboard!");
        credentialsToggle();
    };

    const generateReport = async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(
                `${apiURL1}/superadmin/getAllSuperAdmins?page=1&limit=1000`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            const allSuperAdmins = response.data.superAdmins;

            const doc = new jsPDF();
            const pageWidth = doc.internal.pageSize.getWidth();
            const margin = 10;
            let y = 50;

            const img = new Image();
            img.src = "https://res.cloudinary.com/djg7v9dna/image/upload/v1742946371/mi0wavjsehps20kdzaet.png";
            doc.addImage(img, 'PNG', 15, 15, 30, 30);

            doc.setFontSize(16);
            doc.setFont('helvetica', 'bold');
            doc.text(madrasaInfo.schoolName || 'BLiNX Lanka (PVT) LTD', 50, 22);
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text(madrasaInfo.schoolAddress || 'Colombo, Sri Lanka', 50, 28);
            doc.text(`Tel: ${madrasaInfo.schoolPhoneNumber || '+94 112 345 678'} | Email: ${madrasaInfo.schoolEmail || 'info@blinx.lk'}`, 50, 34);

            doc.setFontSize(18);
            doc.text('Super Admin Report', pageWidth / 2, 55, { align: 'center' });

            const columns = ["Email"];
            const data = allSuperAdmins.map((admin) => [
                admin.email,
                formatDate(admin.createdAt),
                formatDate(admin.updatedAt),
            ]);

            doc.autoTable({
                startY: 65,
                head: [columns],
                body: data,
                margin: { left: 15, right: 15 },
            });

            const totalPages = doc.getNumberOfPages();
            for (let i = 1; i <= totalPages; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(0, 0, 0);
                doc.text(
                    `Generated on: ${new Date().toLocaleDateString()} | ${madrasaInfo.schoolName || 'BLiNX Lanka (PVT) LTD'}`,
                    pageWidth - 40,
                    doc.internal.pageSize.getHeight() - 10
                );
            }

            doc.save('super_admin_report.pdf');
            toast.success("Report generated successfully!");
        } catch (error) {
            console.error("Error generating report:", error);
            toast.error("Failed to generate report!");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <Header />
            <Container className="mt--7" fluid>
                <Row>
                    <div className="col">
                        <Button
                            className="d-block ml-auto mb-2"
                            color="secondary"
                            size="sm"
                            onClick={addToggle}
                        >
                            Add Super Admin
                        </Button>
                        <Card className="shadow">
                            <CardHeader className="border-0 px-0 px-md-2 d-flex align-items-center">
                                <Col xs="auto">
                                    <h3 className="mb-0">Super Admins</h3>
                                </Col>
                                <Col className="d-flex justify-content-end align-items-center">
                                    <FormGroup className="mr-2 mb-0">
                                        <Input
                                            bsSize="sm"
                                            type="text"
                                            placeholder="Filter by Email"
                                            value={filterEmail}
                                            onChange={(e) => setFilterEmail(e.target.value)}
                                        />
                                    </FormGroup>
                                    <Button color="primary" size="sm" onClick={generateReport} disabled={isLoading}>
                                        {isLoading ? <Spinner size="sm" /> : <FaFilePdf />} Generate Report
                                    </Button>
                                </Col>
                            </CardHeader>
                            {isLoading ? (
                                <Spinner size="lg" className="my-5 mx-auto" />
                            ) : (
                                superAdmins.length > 0 ? (
                                    <CustomTable columns={columns} rows={rows} />
                                ) : (
                                    <div className="text-center py-3">No Super Admins found.</div>
                                )
                            )}
                            <CardFooter className="py-4">
                                <nav aria-label="...">
                                    <Pagination className="pagination justify-content-end mb-0">
                                        <ReactPaginate
                                            previousLabel={<i className="previous fa fa-chevron-left" aria-hidden="true"></i>}
                                            nextLabel={<i className="next fa fa-chevron-right" aria-hidden="true"></i>}
                                            breakLabel={"..."}
                                            pageCount={Math.ceil(totalCount / 10)}
                                            onPageChange={handlePageChange}
                                            containerClassName={"pagination justify-content-end mb-0"}
                                            pageClassName={"page-item"}
                                            pageLinkClassName={"page-link"}
                                            activeClassName={"active"}
                                            previousClassName={"page-item"}
                                            nextClassName={"page-item"}
                                            previousLinkClassName={"page-link"}
                                            nextLinkClassName={"page-link"}
                                        />
                                    </Pagination>
                                </nav>
                            </CardFooter>
                        </Card>
                    </div>
                </Row>

                {/* Add Super Admin Modal */}
                <Modal isOpen={addModal} toggle={addToggle}>
                    <ModalHeader className="pb-0">
                        Add Super Admin
                        <Button
                            className="close"
                            onClick={addToggle}
                            style={{ position: 'absolute', top: '10px', right: '10px' }}
                        >
                            <FaTimes />
                        </Button>
                    </ModalHeader>
                    <ModalBody className="pb-0 d-flex flex-column">
                        <Form onSubmit={handleAddSubmit}>
                            <Row className="mt-4 w-100">
                                <FormGroup className="w-100">
                                    <Label>Email</Label>
                                    <Input
                                        placeholder="Enter email"
                                        name="email"
                                        type="email"
                                        autoComplete="new-email"
                                        required
                                        value={addSuperAdminFormData.email}
                                        onChange={handleAddInputChange}
                                        style={{ 
                                            color: addSuperAdminFormData.email ? '#000' : '', 
                                            opacity: 1,
                                            borderColor: emailError ? '#dc3545' : ''
                                        }}
                                    />
                                    {emailError && (
                                        <div style={{ color: '#dc3545', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                                            {emailError}
                                        </div>
                                    )}
                                </FormGroup>
                            </Row>
                            <Row className="mt-4 w-100">
                                <FormGroup className="w-100">
                                    <Label>Password</Label>
                                    <Input
                                        placeholder="Enter password"
                                        type="password"
                                        name="password"
                                        autoComplete="new-password"
                                        required
                                        value={addSuperAdminFormData.password}
                                        onChange={handleAddInputChange}
                                        style={{ color: addSuperAdminFormData.password ? '#000' : '', opacity: 1 }}
                                    />
                                </FormGroup>
                            </Row>
                            <Button
                                color="primary"
                                type="submit"
                                className="mt-4 w-100 mb-10"
                                style={{ marginBottom: "1rem" }}
                            >
                                Add Super Admin
                            </Button>
                        </Form>
                    </ModalBody>
                </Modal>

                {/* Delete Confirmation Modal */}
                <Modal isOpen={deleteModal} toggle={deleteToggle}>
                    <ModalHeader className="pb-0">
                        Delete Super Admin
                        <Button
                            className="close"
                            onClick={deleteToggle}
                            style={{ position: 'absolute', top: '10px', right: '10px' }}
                        >
                            <FaTimes />
                        </Button>
                    </ModalHeader>
                    <ModalBody className="pb-0 d-flex flex-column justify-content-center align-items-center">
                        <p>Are you sure you want to delete this Super Admin?</p>
                        <div className="d-flex justify-content-center" style={{ marginBottom: "1rem" }}>
                            <Button color="danger" onClick={handleDelete} className="mx-2">
                                Yes, Delete
                            </Button>
                            <Button color="secondary" onClick={deleteToggle} className="mx-2">
                                Cancel
                            </Button>
                        </div>
                    </ModalBody>
                </Modal>

                {/* Change Password Modal */}
                <Modal isOpen={changePasswordModal} toggle={changePasswordToggle}>
                    <ModalHeader className="pb-0">
                        Change Password
                        <Button
                            className="close"
                            onClick={changePasswordToggle}
                            style={{ position: 'absolute', top: '10px', right: '10px' }}
                        >
                            <FaTimes />
                        </Button>
                    </ModalHeader>
                    <ModalBody className="pb-0 d-flex flex-column justify-content-center align-items-center">
                        <Form onSubmit={(e) => {
                            e.preventDefault();
                            handleChangePassword();
                        }}>
                            <Row className="mt-4 w-100">
                                <FormGroup className="w-100">
                                    <Label>New Password</Label>
                                    <Input
                                        placeholder="Enter new password"
                                        type="password"
                                        name="newPassword"
                                        required
                                        value={newPassword}
                                        onChange={(e) => setNewPassword(e.target.value)}
                                        style={{ color: newPassword ? '#000' : '', opacity: 1 }}
                                    />
                                </FormGroup>
                            </Row>
                            <Button
                                color="primary"
                                type="submit"
                                className="mt-4 w-100 mb-10"
                                style={{ marginBottom: "1rem" }}
                            >
                                Update Password
                            </Button>
                        </Form>
                    </ModalBody>
                </Modal>

                {/* Credentials Modal */}
                <Modal isOpen={showCredentialsModal} toggle={credentialsToggle}>
                    <ModalHeader>
                        Credentials
                        <Button
                            className="close"
                            onClick={credentialsToggle}
                            style={{ position: 'absolute', top: '10px', right: '10px' }}
                        >
                            <FaTimes />
                        </Button>
                    </ModalHeader>
                    <ModalBody className="pb-0 d-flex flex-column justify-content-center align-items-center">
                        <p>Email: {generatedCredentials.email}</p>
                        <p>Password: {generatedCredentials.password}</p>
                        <Button
                            color="primary"
                            onClick={handleCopyToClipboard}
                            className="mt-4 w-100 mb-10"
                            style={{ marginBottom: "1rem" }}
                        >
                            Copy Credentials
                        </Button>
                    </ModalBody>
                </Modal>
                <Footer />
            </Container>
        </>
    );
};

export default SuperAdminManagement;